import Stack from '@mui/material/Stack';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@shared/ui';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/api/apiSlice';
import {
  type Person,
  useListAllPeopleQuery,
  useListOrganizationsQuery,
} from '../../store/api/apiSlice';
import {
  useArchivePromptMutation,
  useCreatePromptMutation,
  useGetPromptQuery,
  useImportPromptMutation,
  useLazyListPromptsQuery,
  useListPromptsQuery,
  usePromotePromptMutation,
  useRollbackPromptMutation,
  useUpdatePromptMutation,
} from '../../store/api/rpcSlice';
import {
  type BulkImportPlan,
  buildBulkImportPlan,
  fetchExistingPublishedGlobalPrompts,
  parseBulkImportText,
} from './bulkImport';
import { extractPromptErrorMessage, withStaleCaseAcknowledgmentRetry } from './staleAcknowledgment';
import type {
  CreatePromptRequest,
  ImportPromptRequest,
  ListPromptsRequest,
  PromotePromptRequest,
  PromptRecord,
  PromptScope,
  PromptScopeType,
  RollbackPromptRequest,
} from './types';

const actionButtonSx = { height: 34, minHeight: 34, borderRadius: 2 };

type ScopeFilter = PromptScopeType | 'all';

const scopeFilterOptions: { label: string; value: ScopeFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Global', value: 'global' },
  { label: 'Org', value: 'org' },
  { label: 'Person', value: 'person' },
];

// formatScope formats the scope display with org name or person email
// formatScope will be defined inside component to access organizations and people

const getScopeTooltip = (prompt: PromptRecord) => {
  switch (prompt.scope.type) {
    case 'org':
      return prompt.scope.org_id ?? undefined;
    case 'person':
      return prompt.scope.person_id ?? undefined;
    default:
      return undefined;
  }
};

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const serializePromptForImport = (prompt: PromptRecord): ImportPromptRequest => ({
  source_prompt_id: prompt.id,
  slug: prompt.slug,
  scope: prompt.scope,
  text: prompt.text,
  version: prompt.version,
  metadata: prompt.metadata ?? undefined,
  changelog: prompt.changelog ?? undefined,
});

const pluralizePrompts = (count: number) => `${count} prompt${count !== 1 ? 's' : ''}`;

// Bulleted slug list shared by the import review step and the post-import summary.
const PromptSlugSection = ({
  title,
  description,
  color,
  slugs,
}: {
  title: string;
  description: string;
  color: string;
  slugs: string[];
}) => {
  if (slugs.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle1" color={color} fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description}
      </Typography>
      <Box sx={{ mt: 1, pl: 2 }}>
        {slugs.map((slug, index) => (
          <Typography key={`${slug}-${index}`} variant="body2" component="div">
            • {slug}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export {
  requiresStaleCaseAcknowledgment,
  staleCaseAcknowledgmentMessage,
} from './staleAcknowledgment';
export { extractPromptErrorMessage, withStaleCaseAcknowledgmentRetry };

const PromptEditorPage = () => {
  const currentUserId = useSelector((s: RootState) => s.auth.user?.id) || '';
  // Staging detection available via isStaging() when needed for gating features.
  const [slugFilter, setSlugFilter] = useState<string[]>([]);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [orgId, setOrgId] = useState('');
  const [personId, setPersonId] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const [promptChangelog, setPromptChangelog] = useState('');
  const [detailTab, setDetailTab] = useState<'content' | 'metadata' | 'history'>('content');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newDraftSlug, setNewDraftSlug] = useState('');
  const [newDraftScope, setNewDraftScope] = useState<PromptScopeType>('global');
  const [newDraftOrgId, setNewDraftOrgId] = useState('');
  const [newDraftPersonId, setNewDraftPersonId] = useState('');
  const [newDraftText, setNewDraftText] = useState('');
  const [newDraftChangelog, setNewDraftChangelog] = useState('');
  const [newDraftName, setNewDraftName] = useState('');
  const [newDraftDescription, setNewDraftDescription] = useState('');
  const [newDraftError, setNewDraftError] = useState('');
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
  const [promoteScope, setPromoteScope] = useState<PromptScopeType>('global');
  const [promoteOrgId, setPromoteOrgId] = useState('');
  const [promotePersonId, setPromotePersonId] = useState('');
  const [promoteChangelog, setPromoteChangelog] = useState('');
  const [promoteError, setPromoteError] = useState('');
  const [rollbackError, setRollbackError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [isModifying, setIsModifying] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importPlan, setImportPlan] = useState<(BulkImportPlan & { filteredCount: number }) | null>(
    null
  );
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [importSummary, setImportSummary] = useState<{
    imported: string[];
    skipped: string[];
  } | null>(null);
  type SortableColumn = 'metadata' | 'scope' | 'version' | 'status' | 'updated_at';
  const [sortBy, setSortBy] = useState<SortableColumn | null>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch organizations for dropdowns
  const { data: organizations = [], isLoading: isLoadingOrgs } = useListOrganizationsQuery({
    limit: 100,
  });

  // Fetch people for dropdowns - gracefully handle errors (may fail if user doesn't have admin access)
  const {
    data: people = [],
    isLoading: isLoadingPeople,
    error: peopleError,
  } = useListAllPeopleQuery(
    { limit: 1000 },
    {
      // Don't refetch on mount if there was an error (prevents infinite retry loops)
      skip: false,
    }
  );

  // Log but don't block rendering if people query fails
  if (peopleError) {
    console.warn('Failed to load people list (may require admin permissions):', peopleError);
  }

  // Format person display - use email since it's always available
  const formatPersonName = (person: Person) => person.email;

  // Format scope display with org name or person email
  const formatScope = (prompt: PromptRecord) => {
    switch (prompt.scope.type) {
      case 'global':
        return 'Global';
      case 'org':
        if (prompt.scope.org_id) {
          const org = organizations.find((o) => o.id === prompt.scope.org_id);
          return org ? org.name : `Org ${prompt.scope.org_id.slice(0, 8)}`;
        }
        return 'Org';
      case 'person':
        if (prompt.scope.person_id) {
          const person = people.find((p) => p.id === prompt.scope.person_id);
          return person ? person.email : `Person ${prompt.scope.person_id.slice(0, 8)}`;
        }
        return 'Person';
      default:
        return prompt.scope.type;
    }
  };

  const queryArgs: ListPromptsRequest = useMemo(
    () => ({
      slugs: slugFilter.length > 0 ? slugFilter : undefined,
      // When showArchived is true, include deleted (archived) prompts
      // We'll filter on the frontend to show published always, archived optionally
      include_deleted: showArchived,
      scope_type: scopeFilter === 'all' ? undefined : scopeFilter,
      org_id: orgId.trim() || undefined,
      person_id: personId.trim() || undefined,
      limit: 50,
    }),
    [slugFilter, scopeFilter, orgId, personId, showArchived]
  );

  const { data, isFetching, refetch } = useListPromptsQuery(queryArgs);
  const [triggerListPrompts] = useLazyListPromptsQuery();
  const [availableSlugs, setAvailableSlugs] = useState<string[]>([]);
  const [importPrompt] = useImportPromptMutation();

  // Fetch all slugs by paginating through all prompts
  useEffect(() => {
    let isCancelled = false;
    const fetchAllSlugs = async () => {
      const slugs = new Set<string>();
      let offset = 0;
      const limit = 200; // Max allowed by backend
      let hasMore = true;

      while (hasMore && !isCancelled) {
        try {
          const result = await triggerListPrompts({
            limit,
            offset,
            include_deleted: true, // Include archived to get all slugs
          }).unwrap();

          if (!result?.items || result.items.length === 0) {
            hasMore = false;
            break;
          }

          result.items.forEach((prompt) => {
            slugs.add(prompt.slug);
          });

          // If we got fewer items than the limit, we've reached the end
          if (result.items.length < limit) {
            hasMore = false;
          } else {
            offset += limit;
          }
        } catch (error) {
          console.error('Failed to fetch slugs:', error);
          hasMore = false;
        }
      }

      if (!isCancelled) {
        setAvailableSlugs(Array.from(slugs).sort());
      }
    };

    fetchAllSlugs();

    return () => {
      isCancelled = true;
    };
  }, [triggerListPrompts]);
  // Filter prompts: always show published, optionally show archived
  const filteredPrompts = useMemo(() => {
    const allPrompts = data?.items ?? [];
    return allPrompts.filter((prompt) => {
      if (prompt.status === 'published') return true;
      if (prompt.status === 'archived' && showArchived) return true;
      return false;
    });
  }, [data?.items, showArchived]);

  // Sort prompts based on sortBy and sortOrder
  const prompts = useMemo(() => {
    if (!sortBy) return filteredPrompts;

    const sorted = [...filteredPrompts].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'metadata':
          // Sort by name if available, otherwise by slug
          aValue = (a.metadata?.name || a.slug).toLowerCase();
          bValue = (b.metadata?.name || b.slug).toLowerCase();
          break;
        case 'scope':
          // Sort by scope type first, then by org_id/person_id
          if (a.scope.type !== b.scope.type) {
            aValue = a.scope.type;
            bValue = b.scope.type;
          } else {
            aValue = a.scope.org_id || a.scope.person_id || '';
            bValue = b.scope.org_id || b.scope.person_id || '';
          }
          break;
        case 'version':
          aValue = a.version;
          bValue = b.version;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'updated_at':
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredPrompts, sortBy, sortOrder]);

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const selectedFromList = selectedPromptId
    ? prompts.find((prompt) => prompt.id === selectedPromptId)
    : undefined;

  const { data: fetchedPrompt, isFetching: isFetchingPrompt } = useGetPromptQuery(
    { id: selectedPromptId ?? '' },
    { skip: !selectedPromptId }
  );

  // Prioritize list data over fetched data to avoid stale cache issues
  // The list is always up-to-date after mutations, while the query cache might be stale
  const activePrompt = selectedFromList ?? fetchedPrompt ?? null;
  const [viewPrompt, setViewPrompt] = useState<PromptRecord | null>(null);
  const [triggerHistory, { data: historyResponse, isFetching: isHistoryLoading }] =
    useLazyListPromptsQuery();

  useEffect(() => {
    if (!activePrompt) {
      if (!selectedPromptId) {
        setViewPrompt(null);
      }
      return;
    }
    // Always allow viewing published prompts
    // Allow viewing archived prompts when showArchived toggle is enabled
    if (activePrompt.status === 'archived' && !showArchived) {
      setViewPrompt(null);
      return;
    }
    setViewPrompt(activePrompt);
  }, [activePrompt, selectedPromptId, showArchived]);

  useEffect(() => {
    setDetailTab('content');
  }, []);

  useEffect(() => {
    if (!viewPrompt) {
      setPromptText('');
      setPromptChangelog('');
      return;
    }
    setPromptText(viewPrompt.text);
    setPromptChangelog(viewPrompt.changelog ?? '');
  }, [viewPrompt]);

  useEffect(() => {
    if (!selectedPromptId && prompts.length > 0) {
      setSelectedPromptId(prompts[0].id);
    }
  }, [prompts, selectedPromptId]);

  useEffect(() => {
    if (!viewPrompt) return;
    triggerHistory({
      slug: viewPrompt.slug,
      scope_type: viewPrompt.scope.type,
      org_id: viewPrompt.scope.org_id ?? undefined,
      person_id: viewPrompt.scope.person_id ?? undefined,
      include_deleted: true,
      limit: 50,
    });
  }, [viewPrompt, triggerHistory]);

  const historyItems = historyResponse?.items ?? [];

  const [updatePrompt, { isLoading: isSaving }] = useUpdatePromptMutation();
  const [createPrompt, { isLoading: isCreating }] = useCreatePromptMutation();
  const [archivePrompt, { isLoading: isArchiving }] = useArchivePromptMutation();
  const [rollbackPrompt, { isLoading: isRollingBack }] = useRollbackPromptMutation();
  const [promotePrompt, { isLoading: isPromoting }] = usePromotePromptMutation();
  const [checkPersonPrompt] = useLazyListPromptsQuery();

  const isPublished = viewPrompt?.status === 'published';
  const isArchived = viewPrompt?.status === 'archived';

  // Org and person-scoped prompts are editable (for text/changelog)
  // Person-scoped prompts for current user can also edit scope
  // Archived prompts are read-only
  const isEditable =
    !isArchived &&
    (viewPrompt?.scope.type === 'org' || viewPrompt?.scope.type === 'person') &&
    isPublished;

  // Scope editing is allowed for org/person prompts (sideways only)
  const canEditScope = isEditable && viewPrompt?.scope.type !== 'global';

  // Cannot archive global scoped prompts
  const canArchive = isEditable && viewPrompt?.scope.type !== 'global';

  // Can rollback if published (backend will return error if no previous version exists)
  const canRollback = isPublished;

  // State for scope editing
  const [editingOrgId, setEditingOrgId] = useState<string>('');
  const [editingPersonId, setEditingPersonId] = useState<string>('');
  const [scopeError, setScopeError] = useState<string>('');

  // Initialize scope editing state when prompt changes
  useEffect(() => {
    if (viewPrompt) {
      setEditingOrgId(viewPrompt.scope.org_id ?? '');
      setEditingPersonId(viewPrompt.scope.person_id ?? '');
      setScopeError('');
    }
  }, [viewPrompt]);

  const handleSave = async () => {
    if (!viewPrompt || !isEditable) return;
    setScopeError('');

    // Build scope update if scope changed
    let scopeUpdate: PromptScope | undefined;
    if (canEditScope) {
      if (viewPrompt.scope.type === 'org') {
        const newOrgId = editingOrgId.trim();
        if (newOrgId && newOrgId !== viewPrompt.scope.org_id) {
          scopeUpdate = { type: 'org', org_id: newOrgId };
        }
      } else if (viewPrompt.scope.type === 'person') {
        const newPersonId = editingPersonId.trim();
        if (newPersonId && newPersonId !== viewPrompt.scope.person_id) {
          scopeUpdate = { type: 'person', person_id: newPersonId };
        }
      }
    }

    try {
      await updatePrompt({
        id: viewPrompt.id,
        text: promptText,
        changelog: promptChangelog,
        scope: scopeUpdate,
      }).unwrap();
      refetch();
    } catch (error: any) {
      console.error('Failed to update prompt:', error);
      const errorMessage =
        error?.data?.error || error?.data?.message || error?.message || 'Failed to update prompt';

      // Check if it's a scope conflict error
      if (
        errorMessage.includes('already exists for the target scope') ||
        errorMessage.includes('scope conflict')
      ) {
        setScopeError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleArchive = async () => {
    if (!viewPrompt || !isEditable) return;
    try {
      await archivePrompt({ id: viewPrompt.id }).unwrap();
      setSelectedPromptId(null);
      setViewPrompt(null);
      refetch();
    } catch (error: any) {
      console.error('Failed to archive prompt:', error);
      const errorMessage =
        error?.data?.error || error?.data?.message || error?.message || 'Failed to archive prompt';
      alert(errorMessage);
    }
  };

  const handleRollback = async () => {
    if (!viewPrompt) return;
    setRollbackError('');
    try {
      const reactivatedPrompt = await withStaleCaseAcknowledgmentRetry<
        RollbackPromptRequest,
        PromptRecord
      >({ prompt_id: viewPrompt.id }, (payload) => rollbackPrompt(payload).unwrap());
      // Update selected prompt to the newly reactivated version
      setSelectedPromptId(reactivatedPrompt.id);
      refetch();
    } catch (error: any) {
      // RTK Query error structure: {status: 404, data: {error: "message"}}
      // Backend returns: {"error": "no previous version to rollback to"}
      // The base query now ensures error.data is parsed as JSON
      const errorData = error?.data;
      const errorMessage =
        (typeof errorData === 'object' && errorData?.error) ||
        (typeof errorData === 'object' && errorData?.message) ||
        (typeof errorData === 'string' ? errorData : null) ||
        error?.message ||
        'Failed to rollback prompt';

      setRollbackError(errorMessage);
    }
  };

  const resetNewDraftForm = () => {
    setNewDraftSlug('');
    setNewDraftScope('global');
    setNewDraftOrgId('');
    setNewDraftPersonId('');
    setNewDraftText('');
    setNewDraftChangelog('');
    setNewDraftName('');
    setNewDraftDescription('');
    setNewDraftError('');
  };

  const openNewDraftDialog = () => {
    resetNewDraftForm();
    setIsCreateDialogOpen(true);
  };

  const openPromoteDialog = () => {
    if (!viewPrompt) return;
    // Default to global scope for all promotions
    const defaultScope: PromptScopeType = 'global';
    setPromoteScope(defaultScope);
    // Default org_id to empty (will be set if user selects org scope)
    setPromoteOrgId('');
    setPromotePersonId(viewPrompt.scope.person_id ?? '');
    setPromoteChangelog('');
    setPromoteError('');
    setIsPromoteDialogOpen(true);
  };

  const handleSubmitNewPrompt = async () => {
    setNewDraftError('');
    if (!newDraftSlug.trim()) {
      setNewDraftError('Slug is required.');
      return;
    }
    if (!newDraftText.trim()) {
      setNewDraftError('Prompt text is required.');
      return;
    }
    if (newDraftScope === 'org' && !newDraftOrgId.trim()) {
      setNewDraftError('Org ID is required for org scope.');
      return;
    }
    if (newDraftScope === 'person' && !newDraftPersonId.trim()) {
      setNewDraftError('Person ID is required for person scope.');
      return;
    }
    const scope =
      newDraftScope === 'global'
        ? { type: 'global' as const }
        : newDraftScope === 'org'
          ? { type: 'org' as const, org_id: newDraftOrgId.trim() }
          : { type: 'person' as const, person_id: newDraftPersonId.trim() };
    const metadata =
      newDraftName || newDraftDescription
        ? {
            name: newDraftName || undefined,
            description: newDraftDescription || undefined,
          }
        : undefined;
    try {
      const record = await withStaleCaseAcknowledgmentRetry<CreatePromptRequest, PromptRecord>(
        {
          slug: newDraftSlug.trim(),
          scope,
          text: newDraftText,
          changelog: newDraftChangelog.trim() || undefined,
          metadata,
        },
        (payload) => createPrompt(payload).unwrap()
      );
      setSelectedPromptId(record.id);
      setViewPrompt(record);
      setPromptText(record.text);
      setPromptChangelog(record.changelog ?? '');
      resetNewDraftForm();
      setIsCreateDialogOpen(false);
      refetch();
    } catch (error: any) {
      setNewDraftError(extractPromptErrorMessage(error));
    }
  };

  const handleSubmitPromote = async () => {
    if (!viewPrompt) return;
    setPromoteError('');
    if (promoteScope === 'org' && !promoteOrgId.trim()) {
      setPromoteError('Org ID is required for org promotions.');
      return;
    }
    if (promoteScope === 'person' && !promotePersonId.trim()) {
      setPromoteError('Person ID is required for person promotions.');
      return;
    }
    const targetScope =
      promoteScope === 'global'
        ? { type: 'global' as const }
        : promoteScope === 'org'
          ? { type: 'org' as const, org_id: promoteOrgId.trim() }
          : { type: 'person' as const, person_id: promotePersonId.trim() };
    try {
      const record = await withStaleCaseAcknowledgmentRetry<PromotePromptRequest, PromptRecord>(
        {
          source_id: viewPrompt.id,
          target_scope: targetScope,
          changelog: promoteChangelog.trim() || undefined,
          archive_source: true,
        },
        (payload) => promotePrompt(payload).unwrap()
      );
      setSelectedPromptId(record.id);
      setViewPrompt(record);
      setPromptText(record.text);
      setPromptChangelog(record.changelog ?? '');
      setIsPromoteDialogOpen(false);
      setPromoteChangelog('');
      setPromoteError('');
      refetch();
    } catch (error: any) {
      console.error('Failed to promote prompt:', error);
      const errorMessage =
        error?.data?.error || error?.data?.message || error?.message || 'Failed to promote prompt';

      // Extract friendly error message
      let friendlyMessage = errorMessage;
      if (errorMessage.includes('already exists for the target scope')) {
        // Parse the error to extract scope details
        if (errorMessage.includes('org')) {
          const orgMatch = errorMessage.match(/org ([a-f0-9-]+)/i);
          if (orgMatch) {
            friendlyMessage = `A published prompt already exists for organization ${orgMatch[1]}. Please archive or edit the existing prompt first.`;
          } else {
            friendlyMessage =
              'A published prompt already exists for this organization. Please archive or edit the existing prompt first.';
          }
        } else if (errorMessage.includes('person')) {
          const personMatch = errorMessage.match(/person ([a-f0-9-]+)/i);
          if (personMatch) {
            friendlyMessage = `A published prompt already exists for person ${personMatch[1]}. Please archive or edit the existing prompt first.`;
          } else {
            friendlyMessage =
              'A published prompt already exists for this person. Please archive or edit the existing prompt first.';
          }
        } else {
          friendlyMessage =
            'A published prompt already exists for the target scope. Please archive or edit the existing prompt first.';
        }
      }

      setPromoteError(friendlyMessage);
    }
  };

  const handleModify = async (prompt: PromptRecord) => {
    if (!currentUserId) {
      alert('Unable to determine current user. Please refresh the page.');
      return;
    }

    setIsModifying(true);
    try {
      // Check if a person-scoped prompt already exists for this slug and current user
      const existingPersonPrompt = await checkPersonPrompt({
        slug: prompt.slug,
        scope_type: 'person',
        person_id: currentUserId,
        status: 'published',
        limit: 1,
      }).unwrap();

      if (existingPersonPrompt.items && existingPersonPrompt.items.length > 0) {
        // Person-scoped prompt already exists, select it for editing
        const existingPrompt = existingPersonPrompt.items[0];
        setSelectedPromptId(existingPrompt.id);
        setViewPrompt(existingPrompt);
        setPromptText(existingPrompt.text);
        setPromptChangelog(existingPrompt.changelog ?? '');
      } else {
        // No person-scoped prompt exists, create a new version using createPrompt
        // This will increment the version globally (V3 -> V4)
        const targetScope = { type: 'person' as const, person_id: currentUserId };
        const record = await withStaleCaseAcknowledgmentRetry<CreatePromptRequest, PromptRecord>(
          {
            slug: prompt.slug,
            scope: targetScope,
            text: prompt.text,
            changelog: 'Created personal modification',
            metadata: prompt.metadata ?? undefined,
          },
          (payload) => createPrompt(payload).unwrap()
        );
        setSelectedPromptId(record.id);
        setViewPrompt(record);
        setPromptText(record.text);
        setPromptChangelog(record.changelog ?? '');
        refetch();
      }
    } catch (error) {
      console.error('Failed to modify prompt:', error);
      alert('Failed to modify prompt. Please try again.');
    } finally {
      setIsModifying(false);
    }
  };

  const dirty =
    isEditable &&
    viewPrompt &&
    (promptText !== viewPrompt.text ||
      (promptChangelog ?? '') !== (viewPrompt.changelog ?? '') ||
      (viewPrompt.scope.type === 'org' && editingOrgId !== (viewPrompt.scope.org_id ?? '')) ||
      (viewPrompt.scope.type === 'person' &&
        editingPersonId !== (viewPrompt.scope.person_id ?? '')));

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Failed to copy', err);
      return false;
    }
  };

  const handleExportCurrentPrompt = async () => {
    if (!viewPrompt) return;
    const payload = serializePromptForImport(viewPrompt);
    const ok = await copyToClipboard(JSON.stringify(payload, null, 2));
    if (!ok) {
      alert('Failed to copy prompt to clipboard');
    }
  };

  const handleExportAllPublished = async () => {
    const published = (data?.items || []).filter(
      (p) => p.status === 'published' && !p.deleted_at && p.scope.type === 'global'
    );
    if (!published.length) {
      alert('No global published prompts to export.');
      return;
    }
    const payload = published.map(serializePromptForImport);
    const ok = await copyToClipboard(JSON.stringify(payload, null, 2));
    if (!ok) {
      alert('Failed to copy prompts to clipboard');
    }
  };

  const openImportDialog = () => {
    setImportText('');
    setImportError('');
    setImportPlan(null);
    setIsImportDialogOpen(true);
  };

  // Compute what would be imported without writing anything.
  const handlePreviewImport = async () => {
    setImportError('');
    const parsed = parseBulkImportText(importText);
    if (!parsed.ok) {
      setImportError(parsed.error);
      return;
    }

    setIsImporting(true);
    try {
      const existingGlobalPrompts = await fetchExistingPublishedGlobalPrompts(
        parsed.globalPrompts,
        async (request) => triggerListPrompts(request).unwrap()
      );
      setImportPlan({
        ...buildBulkImportPlan(parsed.globalPrompts, existingGlobalPrompts),
        filteredCount: parsed.filteredCount,
      });
    } catch (err: any) {
      console.error('Import preview failed', err);
      setImportError(err?.data?.error || err?.message || 'Import preview failed');
    } finally {
      setIsImporting(false);
    }
  };

  // Import exactly the previewed plan.
  const handleConfirmImport = async () => {
    if (!importPlan) return;
    setImportError('');
    setIsImporting(true);
    try {
      for (const item of importPlan.toImport) {
        await withStaleCaseAcknowledgmentRetry<ImportPromptRequest, PromptRecord>(item, (payload) =>
          importPrompt(payload).unwrap()
        );
      }

      setImportSummary({
        imported: importPlan.toImport.map((p) => p.slug),
        skipped: importPlan.skipped,
      });
      setIsImportDialogOpen(false);
      setIsSummaryDialogOpen(true);
    } catch (err: any) {
      console.error('Import failed', err);
      const message = err?.data?.error || err?.message || 'Import failed';
      // Some prompts may already be imported and the server does not dedupe, so the
      // old plan must not be replayed; a fresh review will skip what already went through.
      setImportPlan(null);
      setImportError(
        `${message}. Import stopped. Review again before retrying — prompts already imported will then be skipped.`
      );
    } finally {
      setIsImporting(false);
      refetch();
    }
  };

  return (
    <>
      <Stack spacing={3}>
        <Box className="flex flex-wrap items-center justify-between gap-3">
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700}>
              Prompt Editor
            </Typography>
            <Typography color="text.secondary">
              Manage prompts across global, organization, and person scopes.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Exports all global scoped published prompts">
              <span>
                <Button
                  startIcon={<Icon>content_copy</Icon>}
                  variant="outlined"
                  onClick={handleExportAllPublished}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Imports all global prompts">
              <span>
                <Button
                  startIcon={<Icon>content_paste</Icon>}
                  variant="outlined"
                  onClick={openImportDialog}
                >
                  Import
                </Button>
              </span>
            </Tooltip>
            <Button
              startIcon={<Icon>note_add</Icon>}
              variant="contained"
              onClick={openNewDraftDialog}
            >
              New Prompt
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <Autocomplete
              multiple
              size="small"
              options={availableSlugs}
              value={slugFilter}
              onChange={(_event, newValue) => {
                setSlugFilter(newValue);
              }}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField {...params} label="Slug" placeholder="Select slugs" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              sx={{ minWidth: 300 }}
            />
            <ToggleButtonGroup
              value={scopeFilter}
              exclusive
              onChange={(_event, value) => {
                if (value) setScopeFilter(value);
              }}
              size="small"
            >
              {scopeFilterOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Autocomplete
              size="small"
              options={organizations}
              value={organizations.find((org) => org.id === orgId) || null}
              onChange={(_event, newValue) => {
                setOrgId(newValue?.id || '');
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingOrgs}
              renderInput={(params) => (
                <TextField {...params} label="Organization" placeholder="Filter by organization" />
              )}
              sx={{ minWidth: 200 }}
            />
            <Autocomplete
              size="small"
              options={people}
              value={people.find((p) => p.id === personId) || null}
              onChange={(_event, newValue) => {
                setPersonId(newValue?.id || '');
              }}
              getOptionLabel={(option) => option.email}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingPeople}
              renderInput={(params) => (
                <TextField {...params} label="Person" placeholder="Filter by person" />
              )}
              sx={{ minWidth: 200 }}
            />
            <IconButton aria-label="Refresh" onClick={() => refetch()}>
              <Icon>refresh</Icon>
            </IconButton>
            <ToggleButton
              value="archived"
              selected={showArchived}
              onChange={() => setShowArchived(!showArchived)}
              size="small"
              color="primary"
            >
              Include Archived
            </ToggleButton>
          </Stack>
        </Paper>

        <Box className="grid gap-4 lg:grid-cols-[minmax(0,45%),minmax(0,55%)]">
          <Card sx={{ minHeight: 400 }}>
            {isFetching && <LinearProgress />}
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'metadata'}
                          direction={sortBy === 'metadata' ? sortOrder : 'asc'}
                          onClick={() => handleSort('metadata')}
                        >
                          Name / Slug
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'scope'}
                          direction={sortBy === 'scope' ? sortOrder : 'asc'}
                          onClick={() => handleSort('scope')}
                        >
                          Scope
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'version'}
                          direction={sortBy === 'version' ? sortOrder : 'asc'}
                          onClick={() => handleSort('version')}
                        >
                          Version
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortOrder : 'asc'}
                          onClick={() => handleSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'updated_at'}
                          direction={sortBy === 'updated_at' ? sortOrder : 'asc'}
                          onClick={() => handleSort('updated_at')}
                        >
                          Updated
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prompts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography color="text.secondary" align="center">
                            No prompts match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      prompts.map((prompt) => {
                        const isSelected = prompt.id === selectedPromptId;
                        const isPersonScopedToCurrentUser =
                          prompt.scope.type === 'person' &&
                          prompt.scope.person_id === currentUserId;
                        return (
                          <TableRow
                            key={prompt.id}
                            hover
                            selected={isSelected}
                            onClick={() => setSelectedPromptId(prompt.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography fontWeight={600}>
                                    {prompt.metadata?.name ?? prompt.slug}
                                  </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {prompt.slug}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const tooltipText = getScopeTooltip(prompt);
                                const chip = <Chip size="small" label={formatScope(prompt)} />;
                                return tooltipText ? (
                                  <Tooltip title={tooltipText}>
                                    <span>{chip}</span>
                                  </Tooltip>
                                ) : (
                                  chip
                                );
                              })()}
                            </TableCell>
                            <TableCell>v{prompt.version}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={prompt.status === 'published' ? 'success' : 'default'}
                                label={prompt.status}
                              />
                            </TableCell>
                            <TableCell>{formatDate(prompt.updated_at)}</TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Tooltip
                                title={
                                  isPersonScopedToCurrentUser
                                    ? 'Edit your personal version'
                                    : 'Create personal modification'
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleModify(prompt)}
                                  disabled={isModifying}
                                  color={isPersonScopedToCurrentUser ? 'primary' : 'default'}
                                >
                                  <Icon>edit</Icon>
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Card sx={{ minHeight: 400 }}>
            {!activePrompt && (isFetchingPrompt || isFetching) && (
              <Skeleton variant="rectangular" height={4} />
            )}
            <CardContent className="flex h-full flex-col gap-3">
              {!viewPrompt ? (
                <Box className="flex h-full flex-col items-center justify-center text-center text-neutral-500">
                  <Icon fontSize="large">description</Icon>
                  <Typography>Select a prompt to view details.</Typography>
                </Box>
              ) : (
                <>
                  <Box className="flex flex-wrap items-start justify-between gap-2">
                    <Box>
                      <Typography variant="h5" fontWeight={600}>
                        {viewPrompt.metadata?.name ?? viewPrompt.slug}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Slug: {viewPrompt.slug} • Version {viewPrompt.version}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Scope:{' '}
                        {viewPrompt.scope.type === 'global' ? (
                          // Global scope is read-only
                          (() => {
                            const tooltipText = getScopeTooltip(viewPrompt);
                            const scopeText = formatScope(viewPrompt);
                            return tooltipText ? (
                              <Tooltip title={tooltipText}>
                                <span style={{ textDecoration: 'underline', cursor: 'help' }}>
                                  {scopeText}
                                </span>
                              </Tooltip>
                            ) : (
                              scopeText
                            );
                          })()
                        ) : viewPrompt.scope.type === 'org' ? (
                          // Org scope is editable
                          <Autocomplete
                            size="small"
                            options={organizations}
                            value={organizations.find((org) => org.id === editingOrgId) || null}
                            onChange={(_event, newValue) => {
                              setEditingOrgId(newValue?.id || '');
                              setScopeError('');
                            }}
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disabled={!canEditScope}
                            loading={isLoadingOrgs}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!scopeError}
                                helperText={scopeError || 'Organization'}
                                sx={{ width: 300, mt: 0.5 }}
                              />
                            )}
                          />
                        ) : (
                          // Person scope is editable
                          <Autocomplete
                            size="small"
                            options={people}
                            value={people.find((p) => p.id === editingPersonId) || null}
                            onChange={(_event, newValue) => {
                              setEditingPersonId(newValue?.id || '');
                              setScopeError('');
                            }}
                            getOptionLabel={(option) => formatPersonName(option)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disabled={!canEditScope}
                            loading={isLoadingPeople}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!scopeError}
                                helperText={scopeError || 'Person'}
                                sx={{ width: 300, mt: 0.5 }}
                              />
                            )}
                          />
                        )}
                      </Typography>
                    </Box>
                    <Chip
                      color={viewPrompt.status === 'published' ? 'success' : 'default'}
                      label={viewPrompt.status}
                    />
                  </Box>
                  <Tabs
                    value={detailTab}
                    onChange={(_event, value) => value && setDetailTab(value)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ mt: 1 }}
                  >
                    <Tab label="Content" value="content" />
                    <Tab label="Metadata" value="metadata" />
                    <Tab label="History" value="history" />
                  </Tabs>
                  <Divider />
                  {detailTab === 'metadata' ? (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Display Name
                      </Typography>
                      <Typography>{viewPrompt.metadata?.name ?? '—'}</Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography>{viewPrompt.metadata?.description ?? '—'}</Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography>{viewPrompt.metadata?.category ?? '—'}</Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tags
                      </Typography>
                      {viewPrompt.metadata?.tags && viewPrompt.metadata.tags.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {viewPrompt.metadata.tags.map((tag) => (
                            <Chip key={tag} size="small" label={tag} />
                          ))}
                        </Stack>
                      ) : (
                        <Typography>—</Typography>
                      )}
                      <Typography variant="subtitle2" color="text.secondary">
                        Validation
                      </Typography>
                      <Box>
                        <Typography variant="body2">
                          Max Length: {viewPrompt.metadata?.validation?.max_length ?? '—'}
                        </Typography>
                        <Typography variant="body2">
                          Required Variables:{' '}
                          {viewPrompt.metadata?.validation?.required_variables &&
                          viewPrompt.metadata.validation.required_variables.length > 0
                            ? viewPrompt.metadata.validation.required_variables.join(', ')
                            : '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : detailTab === 'history' ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Version</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell>Changelog</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isHistoryLoading ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Skeleton height={32} />
                              </TableCell>
                            </TableRow>
                          ) : historyItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography color="text.secondary" align="center">
                                  No history yet.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            historyItems.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>v{item.version}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={item.status}
                                    color={item.status === 'published' ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{formatDate(item.updated_at)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {item.changelog ?? '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Stack spacing={2}>
                      <TextField
                        label="Prompt Text"
                        value={promptText}
                        multiline
                        minRows={8}
                        maxRows={30}
                        onChange={(event) => setPromptText(event.target.value)}
                        disabled={!isEditable}
                        helperText={
                          !isEditable && viewPrompt?.scope.type === 'global'
                            ? 'Global prompts are read-only. Click "Modify" to create a scoped version for editing.'
                            : !isEditable
                              ? 'Only published org and person prompts can be edited.'
                              : undefined
                        }
                      />
                      <TextField
                        label="Changelog"
                        value={promptChangelog}
                        multiline
                        minRows={2}
                        onChange={(event) => setPromptChangelog(event.target.value)}
                        disabled={!isEditable}
                      />
                      {isPublished && (
                        <Stack direction="row" spacing={1}>
                          {isEditable && (
                            <Button
                              variant="contained"
                              size="small"
                              sx={actionButtonSx}
                              onClick={handleSave}
                              disabled={!dirty || isSaving}
                            >
                              Save
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            sx={actionButtonSx}
                            onClick={openPromoteDialog}
                            disabled={
                              isPromoting ||
                              viewPrompt?.scope.type === 'global' ||
                              (isEditable && dirty)
                            }
                            title={
                              viewPrompt?.scope.type === 'global'
                                ? 'Global prompts cannot be promoted'
                                : isEditable && dirty
                                  ? 'Please save your changes before promoting'
                                  : undefined
                            }
                          >
                            Promote
                          </Button>
                          {canRollback && (
                            <Stack spacing={1}>
                              <Button
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={actionButtonSx}
                                onClick={handleRollback}
                                disabled={isRollingBack || (isEditable && dirty)}
                                startIcon={<Icon>undo</Icon>}
                                title={
                                  isEditable && dirty
                                    ? 'Please save your changes before rolling back'
                                    : undefined
                                }
                              >
                                Rollback
                              </Button>
                              {rollbackError && (
                                <Typography
                                  color="error"
                                  variant="caption"
                                  sx={{ fontSize: '0.75rem', maxWidth: 200 }}
                                >
                                  {rollbackError}
                                </Typography>
                              )}
                            </Stack>
                          )}
                          {canArchive && (
                            <Button
                              color="error"
                              variant="outlined"
                              size="small"
                              sx={actionButtonSx}
                              onClick={handleArchive}
                              disabled={isArchiving}
                            >
                              Archive
                            </Button>
                          )}
                          {viewPrompt?.scope.type === 'global' && (
                            <Stack spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={actionButtonSx}
                                startIcon={<Icon>file_download</Icon>}
                                onClick={handleExportCurrentPrompt}
                                disabled={!viewPrompt}
                              >
                                Export
                              </Button>
                            </Stack>
                          )}
                          {isArchived && (
                            <Chip
                              label="Read-only (Archived)"
                              color="default"
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Prompt</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Slug"
              value={newDraftSlug}
              onChange={(event) => setNewDraftSlug(event.target.value)}
            />
            <ToggleButtonGroup
              value={newDraftScope}
              exclusive
              onChange={(_event, value) => value && setNewDraftScope(value)}
              size="small"
            >
              {scopeFilterOptions
                .filter((option) => option.value !== 'all')
                .map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {newDraftScope === 'org' ? (
              <Autocomplete
                options={organizations}
                value={organizations.find((org) => org.id === newDraftOrgId) || null}
                onChange={(_event, newValue) => {
                  setNewDraftOrgId(newValue?.id || '');
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingOrgs}
                renderInput={(params) => (
                  <TextField {...params} label="Organization" placeholder="Select organization" />
                )}
              />
            ) : newDraftScope === 'person' ? (
              <Autocomplete
                options={people}
                value={people.find((p) => p.id === newDraftPersonId) || null}
                onChange={(_event, newValue) => {
                  setNewDraftPersonId(newValue?.id || '');
                }}
                getOptionLabel={(option) => formatPersonName(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingPeople}
                renderInput={(params) => (
                  <TextField {...params} label="Person" placeholder="Select person" />
                )}
              />
            ) : null}
            <TextField
              label="Display Name"
              value={newDraftName}
              onChange={(event) => setNewDraftName(event.target.value)}
            />
            <TextField
              label="Description"
              value={newDraftDescription}
              onChange={(event) => setNewDraftDescription(event.target.value)}
              multiline
              minRows={2}
            />
            <TextField
              label="Prompt Text"
              value={newDraftText}
              onChange={(event) => setNewDraftText(event.target.value)}
              multiline
              minRows={6}
              maxRows={30}
            />
            <TextField
              label="Changelog"
              value={newDraftChangelog}
              onChange={(event) => setNewDraftChangelog(event.target.value)}
              multiline
              minRows={2}
            />
            {newDraftError && (
              <Typography color="error" variant="body2">
                {newDraftError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitNewPrompt} disabled={isCreating}>
            Create Prompt
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isPromoteDialogOpen}
        onClose={() => setIsPromoteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Promote Prompt</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={promoteScope}
              exclusive
              onChange={(_event, value) => value && setPromoteScope(value)}
              size="small"
            >
              {scopeFilterOptions
                .filter((option) => {
                  if (option.value === 'all') return false;
                  if (option.value === viewPrompt?.scope.type) return false; // Can't promote to same scope

                  // Only allow promotion to broader/higher scopes
                  const currentScope = viewPrompt?.scope.type;
                  if (currentScope === 'person') {
                    // Person can promote to org or global
                    return option.value === 'org' || option.value === 'global';
                  } else if (currentScope === 'org') {
                    // Org can only promote to global
                    return option.value === 'global';
                  }
                  // Global can't promote (button already disabled)
                  return false;
                })
                .map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {promoteScope === 'org' ? (
              <Autocomplete
                options={organizations}
                value={organizations.find((org) => org.id === promoteOrgId) || null}
                onChange={(_event, newValue) => {
                  setPromoteOrgId(newValue?.id || '');
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingOrgs}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Organization"
                    placeholder="Select organization"
                  />
                )}
              />
            ) : promoteScope === 'person' ? (
              <Autocomplete
                options={people}
                value={people.find((p) => p.id === promotePersonId) || null}
                onChange={(_event, newValue) => {
                  setPromotePersonId(newValue?.id || '');
                }}
                getOptionLabel={(option) => formatPersonName(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingPeople}
                renderInput={(params) => (
                  <TextField {...params} label="Target Person" placeholder="Select person" />
                )}
              />
            ) : null}
            <TextField
              label="Promotion Changelog"
              value={promoteChangelog}
              onChange={(event) => setPromoteChangelog(event.target.value)}
              multiline
              minRows={2}
            />
            {promoteError && (
              <Typography color="error" variant="body2">
                {promoteError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPromoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitPromote}
            disabled={
              isPromoting ||
              (promoteScope === 'org' && !promoteOrgId.trim()) ||
              (promoteScope === 'person' && !promotePersonId.trim())
            }
          >
            Promote
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isImportDialogOpen}
        onClose={() => {
          // Stay open while reviewing or importing so a late result cannot land on new input.
          if (!isImporting) setIsImportDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{importPlan ? 'Review import' : 'Import prompts'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {importPlan ? (
              <>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Stack spacing={3}>
                    {importPlan.toImport.length > 0 ? (
                      <PromptSlugSection
                        title={`✅ Will import (${pluralizePrompts(importPlan.toImport.length)})`}
                        description="These prompts differ from the current published version and will be imported:"
                        color="success.main"
                        slugs={importPlan.toImport.map((p) => p.slug)}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Nothing to import — every prompt matches the current version.
                      </Typography>
                    )}
                    <PromptSlugSection
                      title={`⏭️ Skipped — identical to current (${pluralizePrompts(importPlan.skipped.length)})`}
                      description="These prompts match the current published version and will be skipped:"
                      color="text.secondary"
                      slugs={importPlan.skipped}
                    />
                  </Stack>
                </Box>
                {importPlan.filteredCount > 0 && (
                  <Typography variant="body2" color="warning.main">
                    {importPlan.filteredCount} non-global prompt(s) ignored. Org and person prompts
                    must be updated manually.
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Global prompts not in this import are left unchanged.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Paste JSON array or single prompt object with fields: slug, scope, text, version,
                  metadata, changelog. Only global-scoped prompts can be imported.
                </Typography>
                <TextField
                  label="Import JSON"
                  value={importText}
                  onChange={(e) => {
                    setImportText(e.target.value);
                    setImportPlan(null);
                  }}
                  multiline
                  minRows={8}
                  disabled={isImporting}
                />
              </>
            )}
            {importError && (
              <Typography color="error" variant="body2">
                {importError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {importPlan ? (
            <>
              <Button onClick={() => setImportPlan(null)} disabled={isImporting}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmImport}
                disabled={isImporting || importPlan.toImport.length === 0}
              >
                {isImporting
                  ? 'Importing…'
                  : `Import ${pluralizePrompts(importPlan.toImport.length)}`}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setIsImportDialogOpen(false)} disabled={isImporting}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handlePreviewImport} disabled={isImporting}>
                {isImporting ? 'Reviewing…' : 'Review'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={isSummaryDialogOpen}
        onClose={() => setIsSummaryDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Import Summary</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {importSummary && (
              <>
                <PromptSlugSection
                  title={`✅ Imported (${pluralizePrompts(importSummary.imported.length)})`}
                  description="These prompts were different from existing ones and have been imported:"
                  color="success.main"
                  slugs={importSummary.imported}
                />
                <PromptSlugSection
                  title={`⏭️ Skipped (${pluralizePrompts(importSummary.skipped.length)})`}
                  description="These prompts were identical to existing ones and were skipped to avoid unnecessary version bumps:"
                  color="text.secondary"
                  slugs={importSummary.skipped}
                />
                {importSummary.imported.length === 0 && importSummary.skipped.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No changes detected.
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIsSummaryDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PromptEditorPage;
